import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Gift, Users } from 'lucide-react';
import { toast } from 'sonner';

const SorteosPage = () => {
  const navigate = useNavigate();
  const { adminSlug } = useParams();
  const [sorteos, setSorteos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminInfo, setAdminInfo] = useState(null);

  useEffect(() => {
    if (adminSlug) {
      fetchAdminInfo();
      fetchSorteos();
    }
  }, [adminSlug]);

  const fetchAdminInfo = async () => {
    try {
      const response = await axios.get(`${API}/admins/public/${adminSlug}`);
      setAdminInfo(response.data);
    } catch (error) {
      console.error('Error fetching admin info:', error);
    }
  };

  const fetchSorteos = async () => {
    try {
      const response = await axios.get(`${API}/sorteos/public/${adminSlug}`);
      setSorteos(response.data);
    } catch (error) {
      console.error('Error fetching sorteos:', error);
      if (error.response?.status === 404) {
        toast.error('Organizador no encontrado');
      }
    } finally {
      setLoading(false);
    }
  };

  const getAvailableCount = (sorteo) => sorteo.stats?.available || 0;

  // Estilo de fondo personalizado
  const getBackgroundStyle = () => {
    const primaryColor = adminInfo?.primary_color || '#22c55e'; // Verde por defecto para sorteos
    const backgroundImage = adminInfo?.background_image;

    if (backgroundImage) {
      return {
        backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      };
    }

    return {
      background: `linear-gradient(135deg, ${primaryColor} 0%, #059669 50%, #0d9488 100%)`
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-600 via-emerald-500 to-teal-500 flex items-center justify-center">
        <div className="text-white text-2xl">Cargando sorteos...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8" style={getBackgroundStyle()}>
      <div className="max-w-7xl mx-auto">
        <Button
          variant="outline"
          onClick={() => navigate('/')}
          className="mb-6 bg-white/10 text-white hover:bg-white/20 border-white/30"
        >
          <ArrowLeft className="mr-2" size={20} />
          Volver al Inicio
        </Button>

        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-white mb-4">
            {adminInfo?.name ? `Sorteos de ${adminInfo.name}` : 'Sorteos Activos'}
          </h1>
          <p className="text-xl text-white/80">
            ¡Participá gratis! Solo 1 número por persona
          </p>
          <Badge className="mt-4 bg-white/20 text-white text-lg px-4 py-2">
            🎁 GRATIS
          </Badge>
        </div>

        {sorteos.length === 0 ? (
          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardContent className="p-12 text-center">
              <Gift size={64} className="mx-auto mb-4 text-white/50" />
              <p className="text-white text-xl">No hay sorteos activos en este momento</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sorteos.map((sorteo) => (
              <Card
                key={sorteo.id}
                className="bg-white/95 backdrop-blur-lg hover:shadow-2xl transition-all cursor-pointer"
                onClick={() => navigate(`/sorteo/${sorteo.id}`)}
              >
                <CardHeader>
                  {sorteo.logo && (
                    <div className="mb-4">
                      <img
                        src={sorteo.logo}
                        alt={sorteo.title}
                        className="w-full h-32 object-contain"
                      />
                    </div>
                  )}
                  <CardTitle className="text-2xl flex items-center gap-2">
                    <Gift className="text-green-600" />
                    {sorteo.title}
                  </CardTitle>
                  <CardDescription>
                    <Badge className="mt-2 bg-green-600">GRATIS</Badge>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 flex items-center">
                      <Users size={18} className="mr-1" />
                      Disponibles:
                    </span>
                    <span className="text-xl font-bold text-green-600">
                      {getAvailableCount(sorteo)} / {sorteo.total_numbers}
                    </span>
                  </div>
                  <div className="pt-2">
                    <p className="text-sm font-semibold text-gray-700 mb-1">🏆 Premios:</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {sorteo.prizes.slice(0, 3).map((prize, idx) => (
                        <li key={idx}>• {prize}</li>
                      ))}
                      {sorteo.prizes.length > 3 && (
                        <li className="text-green-600 font-semibold">
                          + {sorteo.prizes.length - 3} premios más
                        </li>
                      )}
                    </ul>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button className="w-full bg-green-600 hover:bg-green-700">
                    ¡Participar Gratis!
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SorteosPage;
